import React from 'react';
import cn from 'classnames';
import type { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import { CarouselItem } from '../Carousel';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image from 'tbk-components/src/components/Image';
import { colorMap, bgColors } from '../../utils';
import SliderItem, { ISliderItemProps } from './SliderItem';
import Carousel from './Carousel';

import logoSymbol from '../../assets/logo-symbol.svg';

export interface ITextAndSliderProps extends IBaseElementProps {
	backgroundColor?: bgColors;
	decorType?: 'a' | 'b' | 'none';
	useCarousel?: boolean;
	/**
	 * Forces all slider items to have a white background and removes the yellow arrow button.
	 * Only works when using carousel.
	 */
	useAlternativeItemLayout?: boolean;
	showLogo?: boolean;
	heading?: string;
	content?: string;
	button?: ILink;
	sliderItems?: ISliderItemProps[];
}

/**
 * Bar with text and slider
 * @block
 * @requiresHooks
 */
const TextAndSlider: React.FC<ITextAndSliderProps> = ({
	backgroundColor = 'white',
	className,
	classNames = [],
	content,
	decorType = 'none',
	heading,
	button,
	sliderItems = [],
	showLogo,
	useCarousel,
	useAlternativeItemLayout = false,
}) => {
	const carouselOptions = {
		type: 'slider',
		rewind: false,
		bound: true,
		gap: 32,
		perView: 2,
		peek: {
			before: 0,
			after: 180,
		},
		breakpoints: {
			640: {
				perView: 1,
				peek: {
					before: 0,
					after: useAlternativeItemLayout ? 57 : 115,
				},
			},
			765: {
				perView: 2,
				peek: {
					before: 0,
					after: useAlternativeItemLayout ? 80 : 115,
				},
			},
			900: {
				perView: 1,
				peek: {
					before: 0,
					after: 100,
				},
			},
			1140: {
				gap: 16,
				perView: 2,
				peek: {
					before: 0,
					after: 115,
				},
			},
		},
	};

	return (
		<div
			className={
				className ||
				cn(
					'two-col-slider',
					`two-col-slider--bg-${colorMap[backgroundColor]}`,
					`two-col-slider--decor-${decorType}`,
					'container-fluid',
					...classNames,
				)
			}
		>
			<div className="two-col-slider__container row">
				<div
					className={cn(
						'two-col-slider__content-wrapper',
						'col-12',
						'col-md-auto',
						backgroundColor === 'dark' && 'text-light',
					)}
				>
					{showLogo && (
						<Image
							src={logoSymbol}
							width={64}
							height={64}
							className="logo"
						/>
					)}
					{heading && <h2>{heading}</h2>}
					{content && (
						<p
							className={cn(
								backgroundColor === 'dark' && 'text-light',
							)}
						>
							{content}
						</p>
					)}
					{button?.label?.length > 0 && <Button {...button} />}
				</div>
				<div
					className={cn(
						'two-col-slider__slider-wrapper',
						'col-12',
						'col-md',
						!useCarousel && 'slides',
					)}
				>
					{useCarousel ? (
						<Carousel
							showArrows={sliderItems.length > 2}
							showBullets={false}
							perView={2}
							options={carouselOptions}
							fadeColor={backgroundColor}
							withDecor={decorType !== 'none'}
						>
							{sliderItems.map((item, index) => (
								<CarouselItem>
									<SliderItem
										itemBackgroundColor={
											useAlternativeItemLayout
												? 'white'
												: null
										}
										{...item}
										key={index}
										altLayout={useAlternativeItemLayout}
									/>
								</CarouselItem>
							))}
						</Carousel>
					) : (
						<div className="side-scroll">
							{sliderItems.map(
								(item, index) =>
									item && (
										<SliderItem
											{...item}
											itemBackgroundColor={null}
											image={null}
											key={index}
										/>
									),
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default TextAndSlider;
